import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {AmountType} from "crqlar-react-common/dist/models/common/amountType.model";
import {Rate} from "crqlar-react-common/dist/models/rate/rate.model";
import {TaxRate} from "crqlar-react-common/dist/models/rate/taxRate.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {addMinutes} from "crqlar-react-common/dist/utils/date.util";

/** The backend reads amountCents only, so an amount without cents is converted. */
export const amountCentsOf = (amount?: AmountType): number | undefined => {
    if (amount?.amountCents != null) return amount.amountCents;
    return amount?.amount != null ? Math.round(amount.amount * 100) : undefined;
};

/** A line price in the folio's currency; without cents it's a price still to be set. */
export const priceOfCents = (cents: number | undefined, currency: string): AmountType => (
    cents == null ? {currency} : {amountCents: cents, amount: cents / 100, currency}
);

export interface RateVat {
    taxRateId: string
    taxRateSnap?: TaxRate
}

/**
 * The VAT of a rate: the raw rate document carries it, the calculated one only sometimes. The id and
 * its snapshot always go together; the snapshot comes from the tax rate list when it is known there,
 * as the backend resolves it from the id on save.
 */
export const rateVat = (calculatedRate?: Rate, rawRate?: Rate, taxRates?: TaxRate[]): RateVat | undefined => {
    const taxRateId = rawRate?.taxRateId || rawRate?.taxRateSnap?.id
        || calculatedRate?.taxRateId || calculatedRate?.taxRateSnap?.id;
    if (!taxRateId) return undefined;

    const snapshot = taxRates?.find(taxRate => taxRate.id === taxRateId)
        ?? [rawRate?.taxRateSnap, calculatedRate?.taxRateSnap].find(snap => snap?.id === taxRateId);
    return {taxRateId, taxRateSnap: snapshot};
};

export type AutofillWarning = {kind: 'NO_RATE', date: Date};

export interface ServiceAutofillInput {
    service: Service
    /** The service's calculated rate on the line's date, which carries the price. */
    calculatedRate?: Rate
    /** The raw document of that rate, which carries the VAT. */
    rawRate?: Rate
    /** The organization's tax rates, to resolve a VAT id to its snapshot. */
    taxRates?: TaxRate[]
    /** When the line starts: the rate's date, and the start of the service's duration. */
    start: Date
    currency: string
}

export interface ServiceAutofill {
    patch: Partial<FolioItem>
    warning?: AutofillWarning
}

/**
 * What picking a service puts on a line: its name, id and market code, the end after its duration,
 * the price of its rate on the line's date (none when there is no rate - the line is flagged then)
 * and the rate's VAT. A rate without VAT leaves the line's VAT alone, as the repricing does.
 */
export const serviceAutofill = (input: ServiceAutofillInput): ServiceAutofill => {
    const {service, calculatedRate, rawRate, taxRates, start, currency} = input;
    const cents = amountCentsOf(calculatedRate?.price);
    const vat = rateVat(calculatedRate, rawRate, taxRates);

    const patch: Partial<FolioItem> = {
        description: service.name ?? '',
        serviceId: service.id,
        serviceInventoryCode: service.marketCode ?? '',
        price: priceOfCents(cents, currency),
        ...(service.durationMinutes != null && {timeSpanEnd: addMinutes(start, service.durationMinutes)}),
        ...vat,
    };

    return {patch, warning: cents == null ? {kind: 'NO_RATE', date: start} : undefined};
};
